import { useRef, useState, type RefObject } from "react";
import type { DropdownMenuProps } from "../../DropdownMenu/types";

type UseListCardActionsMenuParams = {
  actionsMenuContentProps?: DropdownMenuProps["contentProps"];
  actionsMenuOpen?: boolean;
  onActionsMenuOpenChange?: (isOpen: boolean) => void;
};

type UseListCardActionsMenuReturn = {
  triggerRef: RefObject<HTMLButtonElement>;
  isActionsPinnedVisible: boolean;
  suppressActionsReveal: boolean;
  isMenuOpen: boolean;
  isMenuOpenControlled: boolean;
  handleMenuOpenChange: (isOpen: boolean) => void;
  resetActionsReveal: () => void;
  menuContentProps: DropdownMenuProps["contentProps"];
};

/**
 * Manages hover-reveal actions menu state for `ListCard`.
 *
 * The trigger stays visible while the menu is open, then hides after close until
 * the pointer re-enters the card (so toggle-close does not leave a focused button).
 */
export const useListCardActionsMenu = ({
  actionsMenuContentProps,
  isBusy = false,
  actionsMenuOpen,
  onActionsMenuOpenChange,
}: UseListCardActionsMenuParams & {
  isBusy?: boolean;
}): UseListCardActionsMenuReturn => {
  const [uncontrolledMenuOpen, setUncontrolledMenuOpen] = useState(false);
  const [suppressActionsReveal, setSuppressActionsReveal] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isMenuOpenControlled = actionsMenuOpen !== undefined;
  const isMenuOpen = isMenuOpenControlled
    ? actionsMenuOpen
    : uncontrolledMenuOpen;

  const { onCloseAutoFocus, ...restActionsMenuContentProps } =
    actionsMenuContentProps ?? {};

  const handleMenuOpenChange = (isOpen: boolean) => {
    if (!isMenuOpenControlled) {
      setUncontrolledMenuOpen(isOpen);
    }

    onActionsMenuOpenChange?.(isOpen);

    if (isOpen) {
      setSuppressActionsReveal(false);
      return;
    }

    triggerRef.current?.blur();
    setSuppressActionsReveal(true);
  };

  const resetActionsReveal = () => {
    setSuppressActionsReveal(false);
  };

  const menuContentProps: DropdownMenuProps["contentProps"] = {
    ...restActionsMenuContentProps,
    onCloseAutoFocus: (event) => {
      event.preventDefault();
      onCloseAutoFocus?.(event);
    },
  };

  return {
    triggerRef,
    isActionsPinnedVisible: isBusy || isMenuOpen,
    suppressActionsReveal,
    isMenuOpen,
    isMenuOpenControlled,
    handleMenuOpenChange,
    resetActionsReveal,
    menuContentProps,
  };
};
